import { telemetryIncrementAction } from 'core-events/actions';
import { JSX, useContext, useEffect, useState } from 'react';
import { StatsigExperimentContext } from 'statsig/components';
import { twMerge } from 'tailwind-merge';

import { Search } from '@videoblocks/react-icons';

import { useConversationalSearch } from '../../context/NavSearchContext';
import { getConversationalSearchUnavailableMessage } from '../ProminentSearchElements/conversationalSearchMessaging';
import { SPARKLE_ICON_PATHS, SPARKLE_ICON_VIEWBOX } from '../ProminentSearchElements/sparkleIcon';

// Unlike the search-page toggle, this one is click-only by requirement:
// no auto-enable-on-hover and no tooltips in the top nav.

// Inline background rather than an SVG gradient fill: the nav renders the bar
// twice (desktop + mobile instances), and a url(#id) fill can be captured by a
// CSS-hidden duplicate's <defs>; an inline background cannot.
const AI_GRADIENT = 'linear-gradient(122deg, #9952e0 0%, #cc5ce3 50%, #ff66e6 100%)';

const HOVER_GLOW_CLS = 'shadow-[0_0_12px_2px_rgba(204,92,227,0.5)]';

const SEGMENT_CLS =
    'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-200';

const ICON_CLS = 'w-[13px] h-[13px] fill-current';

interface TopNavSearchToggleProps {
    disabledForContentType?: boolean;
}

function SparkleIcon({ className }: { className?: string }): JSX.Element {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={SPARKLE_ICON_VIEWBOX}
            fill="currentColor"
            className={twMerge('w-[13px] h-[13px]', className)}
            aria-hidden="true"
        >
            {SPARKLE_ICON_PATHS.map((d) => (
                <path key={d} d={d} />
            ))}
        </svg>
    );
}

export default function TopNavSearchToggle({ disabledForContentType = false }: TopNavSearchToggleProps): JSX.Element {
    const { conversationalSearchEnabled, setConversationalSearchEnabled: setEnabled } = useConversationalSearch();
    const { showConversationalSearchAudio } = useContext(StatsigExperimentContext);

    const [hovered, setHovered] = useState(false);

    // The disabled branch renders without mouse handlers, so a mid-hover switch
    // to an unsupported content type would leave the glow stuck on for the next
    // enabled render — reset it here.
    useEffect(() => {
        if (disabledForContentType) {
            setHovered(false);
        }
    }, [disabledForContentType]);

    if (disabledForContentType) {
        return (
            <button
                type="submit"
                data-testid="topnav-search-toggle"
                data-mode="no-ai"
                aria-label="Search"
                title={getConversationalSearchUnavailableMessage(showConversationalSearchAudio ?? false)}
                className={twMerge(SEGMENT_CLS, 'cursor-pointer bg-gray-800 hover:bg-gray-900')}
            >
                <Search className={twMerge(ICON_CLS, 'text-white')} />
            </button>
        );
    }

    const handleToggle = (): void => {
        setEnabled((prev) => !prev);
        void telemetryIncrementAction('top_nav_conversational_search_toggle_click');
    };

    return (
        <button
            type="button"
            data-testid="topnav-search-toggle"
            data-mode={conversationalSearchEnabled ? 'ai' : 'standard'}
            aria-label="Toggle AI search"
            aria-pressed={conversationalSearchEnabled}
            onClick={handleToggle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={twMerge(
                'relative flex h-10 flex-shrink-0 cursor-pointer items-center gap-0.5 rounded-full bg-gray-100 p-1 transition-shadow duration-200',
                hovered && HOVER_GLOW_CLS,
            )}
        >
            {/* Active indicator slides between the segments; its solid/gradient
                layers crossfade via opacity because background-image itself
                cannot be transitioned. Travel = one 32px (w-8) segment + the
                2px (gap-0.5) gap. */}
            <span
                aria-hidden="true"
                data-testid="topnav-search-active"
                className={twMerge(
                    'absolute left-1 top-1 h-8 w-8 transition-transform duration-200 ease-in-out',
                    conversationalSearchEnabled ? 'translate-x-[34px]' : 'translate-x-0',
                )}
            >
                <span
                    data-testid="topnav-search-active-standard"
                    className={twMerge(
                        'absolute inset-0 rounded-full bg-gray-800 transition-opacity duration-200',
                        conversationalSearchEnabled ? 'opacity-0' : 'opacity-100',
                    )}
                />
                <span
                    data-testid="topnav-search-active-ai"
                    className={twMerge(
                        'absolute inset-0 rounded-full transition-opacity duration-200',
                        conversationalSearchEnabled ? 'opacity-100' : 'opacity-0',
                    )}
                    style={{ backgroundImage: AI_GRADIENT }}
                />
            </span>
            {/* Segments are relative so their icons paint above the absolutely
                positioned indicator (static elements paint below positioned ones). */}
            <span data-testid="topnav-search-standard" className={twMerge(SEGMENT_CLS, 'relative')}>
                <Search className={twMerge(ICON_CLS, conversationalSearchEnabled ? 'text-gray-700' : 'text-white')} />
            </span>
            <span data-testid="topnav-search-ai" className={twMerge(SEGMENT_CLS, 'relative')}>
                <SparkleIcon className={conversationalSearchEnabled ? 'text-white' : 'text-gray-700'} />
            </span>
        </button>
    );
}
